import React, { useState } from 'react';
import { 
  LayoutDashboard, Users, BookOpen, CreditCard, Mail, 
  MessageSquare, Trophy, DollarSign, Settings, BarChart3,
  Megaphone, Activity, Gift, ShieldCheck, Database, FileText, MessageCircle, MonitorPlay
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  AdminDashboard,
  AdminUsers,
  AdminComics,
  AdminPayments,
  AdminFeedback,
  AdminAnnouncements,
  AdminMessages,
  AdminSettings,
  AdminBlog,
  AdminCreatorMessages,
  AdminSubscriptions
} from '../components/admin';
import { AdminAds } from '../components/admin/AdminAds';

// Sidebar Navigation Configuration
const ADMIN_SECTIONS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
  { id: 'ads', label: 'Google Ads', icon: MonitorPlay },
  { id: 'comics', label: 'Comics', icon: BookOpen },
  { id: 'blog', label: 'Blog', icon: FileText },
  { id: 'creator-messages', label: 'Creator Chat', icon: MessageCircle },
  { id: 'payments', label: 'Payments', icon: DollarSign },
  { id: 'feedback', label: 'Feedback', icon: MessageSquare },
  { id: 'announcements', label: 'Announcements', icon: Megaphone },
  { id: 'messages', label: 'Contact Messages', icon: Mail },
  { id: 'settings', label: 'Settings', icon: Settings },
];

function AdminPage() {
  const { isAdmin, user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ShieldCheck size={64} className="mx-auto text-[var(--primary)] mb-4" />
          <h1 className="text-3xl font-teko mb-2">Access Denied</h1>
          <p className="text-[var(--text-secondary)]">Admin privileges required</p>
        </div>
      </div>
    );
  }

  const renderSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return <AdminDashboard />;
      case 'users':
        return <AdminUsers />;
      case 'subscriptions':
        return <AdminSubscriptions />;
      case 'ads':
        return <AdminAds />;
      case 'comics':
        return <AdminComics />;
      case 'blog':
        return <AdminBlog />;
      case 'creator-messages':
        return <AdminCreatorMessages />;
      case 'payments':
        return <AdminPayments />;
      case 'feedback':
        return <AdminFeedback />;
      case 'announcements':
        return <AdminAnnouncements />;
      case 'messages':
        return <AdminMessages />;
      case 'settings':
        return <AdminSettings />;
      default:
        return <AdminDashboard />;
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="admin-page">
      {/* Sidebar */}
      <aside 
        className={`bg-[var(--surface)] border-r border-[var(--border)] transition-all duration-300 ${
          sidebarCollapsed ? 'w-16' : 'w-64'
        }`}
      >
        <div className="p-4 border-b border-[var(--border)]">
          <button 
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full text-left"
          >
            {sidebarCollapsed ? (
              <span className="text-xl font-teko text-[var(--primary)]">S</span>
            ) : (
              <div>
                <h1 className="text-xl font-teko text-[var(--primary)]">Admin Panel</h1>
                <p className="text-xs text-[var(--text-secondary)]">
                  {isSuperAdmin ? 'Super Admin' : 'Administrator'}
                </p>
              </div>
            )}
          </button>
        </div>

        <nav className="p-2">
          {ADMIN_SECTIONS.map(section => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg mb-1 transition-colors ${
                  isActive 
                    ? 'bg-[var(--primary)] text-white' 
                    : 'hover:bg-[var(--background)] text-[var(--text-secondary)]'
                }`}
                title={sidebarCollapsed ? section.label : undefined}
              >
                <Icon size={20} />
                {!sidebarCollapsed && (
                  <span className="text-sm font-medium">{section.label}</span>
                )}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto bg-[var(--background)]">
        <div className="p-6 pt-20 max-w-7xl mx-auto">
          {renderSection()}
        </div>
      </main>
    </div>
  );
}

export default AdminPage;
